import { Controller } from "@hotwired/stimulus"

// Listens for Mercure-pushed ad state changes and reloads the ads list frame.
//
// Each ad create/update/status-change publishes a turbo-stream to
// `RealtimeTopic.user(id, "ads")` (`/rt/u/<id>/ads`) targeting
// `ads-refresh-<autoboost_id>`. We intercept
// `turbo:before-stream-render`, match on the target element ID, and
// call _reloadFrame() to fetch fresh HTML for the turbo-frame.
//
// Events are debounced (2s window) to coalesce bursts of ad changes.
//
// Previously this controller polled `/autoboosts/:id/ads_timestamp` every 15s.
// The Mercure push replaces the timer-based polling.
export default class extends Controller {
  static values = {
    autoboostId: String,
  }

  connect() {
    this._onStream = this._onStream.bind(this)
    document.addEventListener("turbo:before-stream-render", this._onStream)
    this._debounceTimer = null
    console.log("[ads-poller] connected, waiting for stream events")
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this._onStream)
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
  }

  _onStream(event) {
    const newStream = event.detail?.newStream
    if (!newStream) return
    const target = newStream.getAttribute("target")
    if (target !== `ads-refresh-${this.autoboostIdValue}`) return

    event.preventDefault()

    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => {
      this._reloadFrame()
      this._debounceTimer = null
    }, 2000)
  }

  async _reloadFrame() {
    try {
      const res = await fetch(window.location.href, {
        headers: {
          "Turbo-Frame": this.element.id,
          Accept: "text/html",
        },
        credentials: "same-origin",
      })
      if (!res.ok) return

      const html = await res.text()
      const doc = new DOMParser().parseFromString(html, "text/html")
      const newFrame = doc.getElementById(this.element.id)
      if (!newFrame) return

      this.element.innerHTML = newFrame.innerHTML
    } catch (_e) {
      // Ignore reload errors — will retry on next stream event
    }
  }
}
